import {
  clampMenuToViewport,
  ContextMenuItem,
  ContextMenuSurface,
  KebabButton,
} from '@src/components/common/contextMenuStyles';
import EnlargedImageRenderProp from '@src/components/common/EnlargedImageRenderProp';
import { ENLARGED_HOVER_PREVIEW_Z } from '@src/components/common/topLayerZ';
import SimpleLoadingSpinner from '@src/components/SimpleLoadingSpinner';
import CameraIcon from '@src/svgs/CameraIcon';
import DeleteIcon from '@src/svgs/DeleteIcon';
import MoreVertIcon from '@src/svgs/MoreVertIcon';
import SparkleIcon from '@src/svgs/SparkleIcon';
import { StarFilledIcon } from '@src/svgs/StarFilledIcon';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

interface SpeakerHeadshotGridProps {
  /** Preview URLs in order; the first is the primary, the one nametags show */
  allHeadshotPreviews: string[];
  /** Whether an upload is currently in progress */
  isUploadingHeadshot: boolean;
  /** Remove the headshot at this position */
  onRemove: (index: number) => void;
  /** Make the headshot at this position the primary */
  onSetPrimary: (index: number) => void;
  /** Trigger the file-picker dialog */
  onAddClick: () => void;
}

/**
 * The speaker's headshots (PLAT-12178): round, all one size, one under
 * another. The first is the primary — the one nametags and quote cards show —
 * and its edge is gold (`--accent`, the design system's "chosen", at the
 * weight of a selected chip). The list ends in a dashed circle with the camera, the one way to
 * add a headshot, which is also all there is when there are none. Hovering a
 * headshot shows it larger, round, beside the dialog (the app's
 * `EnlargedImageRenderProp`) and its ⋮ button, whose menu says it plainly:
 * "Set as Primary" and "Remove Headshot" (the primary's menu marks it
 * Primary).
 */
const SpeakerHeadshotGrid: React.FC<SpeakerHeadshotGridProps> = ({
  allHeadshotPreviews,
  isUploadingHeadshot,
  onRemove,
  onSetPrimary,
  onAddClick,
}) => {
  // A headshot with the hover-enlarge preview and its actions menu.
  const renderPhoto = (src: string, index: number) => (
    <EnlargedImageRenderProp
      key={src}
      src={src}
      smallPhotoHeight={SIZE}
      enlargedPhotoHeight={240}
      round
    >
      {({ enlargedState, setEnlargedState, renderEnlargedImage }) => (
        <PhotoFrame
          $primary={index === 0}
          data-primary={index === 0 || undefined}
          title={
            index === 0
              ? 'Primary headshot: it appears on nametags and quote cards.'
              : undefined
          }
          onMouseEnter={e =>
            setEnlargedState(e.currentTarget.getBoundingClientRect())
          }
          onMouseLeave={() => setEnlargedState(null)}
        >
          <Photo
            src={src}
            alt={`Speaker headshot ${index + 1}`}
            $primary={index === 0}
          />
          <HeadshotActionsMenu
            index={index}
            onOpen={() => setEnlargedState(null)}
            onSetPrimary={() => onSetPrimary(index)}
            onRemove={() => onRemove(index)}
          />
          {enlargedState && renderEnlargedImage()}
        </PhotoFrame>
      )}
    </EnlargedImageRenderProp>
  );

  return (
    <HeadshotFormGroup>
      <HeadshotLabel>Headshot</HeadshotLabel>
      <HeadshotList>
        {allHeadshotPreviews.map((preview, i) => renderPhoto(preview, i))}
        <AddTile type="button" aria-label="Add headshot" onClick={onAddClick}>
          {isUploadingHeadshot ? (
            <SparkleLoading />
          ) : (
            <CameraIcon width="20" height="15" />
          )}
        </AddTile>
      </HeadshotList>
    </HeadshotFormGroup>
  );
};

export default SpeakerHeadshotGrid;

const MENU_WIDTH = 190;
const MENU_HEIGHT = 84;

/**
 * A headshot's ⋮ menu (PLAT-12178), the app's row-actions pattern
 * (`SavedSegmentRowActionsMenu`): a `KebabButton` on the photo's top-right
 * edge, shown on hover or focus, opening a body-portaled `ContextMenuSurface`
 * with labelled items. The primary's menu marks it Primary instead of
 * offering Set as Primary. Lifted above the dialog.
 *
 * It closes the way menus do: a click anywhere outside it, Escape, Tab, a
 * scroll or a resize, or picking an item. The outside click is heard on the
 * document's capture phase, because the speaker dialog swallows `mousedown`
 * in capture to keep the editor's selection; and a click on the dialog's
 * backdrop only closes the menu — it would otherwise close the dialog too and
 * lose unsaved edits. Opening focuses the first item; ↑/↓ move between items.
 */
const HeadshotActionsMenu: React.FC<{
  index: number;
  onOpen: () => void;
  onSetPrimary: () => void;
  onRemove: () => void;
}> = ({ index, onOpen, onSetPrimary, onRemove }) => {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(
    null,
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setPosition(null), []);

  useEffect(() => {
    if (!position) return undefined;
    const inside = (target: EventTarget | null) =>
      target instanceof Node &&
      (!!menuRef.current?.contains(target) ||
        !!triggerRef.current?.contains(target));
    // The dialog this menu's trigger sits in (its content box, not the
    // backdrop around it).
    const dialog = triggerRef.current?.closest('[data-selection-menu="true"]');
    const swallowClick = (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
    };
    const onPointerDown = (event: PointerEvent) => {
      if (inside(event.target)) return;
      if (
        dialog &&
        event.target instanceof Node &&
        !dialog.contains(event.target)
      )
        document.addEventListener('click', swallowClick, {
          capture: true,
          once: true,
        });
      close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
        triggerRef.current?.focus();
        return;
      }
      if (event.key === 'Tab') {
        close();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const items = Array.from(
        menuRef.current?.querySelectorAll<HTMLButtonElement>(
          '[role="menuitem"]:not([aria-disabled="true"])',
        ) ?? [],
      );
      if (!items.length) return;
      event.preventDefault();
      const at = items.indexOf(document.activeElement as HTMLButtonElement);
      const next =
        event.key === 'ArrowDown'
          ? (at + 1) % items.length
          : (at - 1 + items.length) % items.length;
      items[next].focus();
    };
    const onScroll = (event: Event) => {
      if (inside(event.target)) return;
      close();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    // Focus the first item the menu offers.
    const frame = requestAnimationFrame(() =>
      menuRef.current
        ?.querySelector<HTMLButtonElement>(
          '[role="menuitem"]:not([aria-disabled="true"])',
        )
        ?.focus(),
    );
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [position, close]);

  const toggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (position) return close();
    onOpen();
    const rect = event.currentTarget.getBoundingClientRect();
    setPosition(
      clampMenuToViewport(rect.left, rect.bottom + 4, MENU_WIDTH, MENU_HEIGHT),
    );
  };

  const run = (action: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    close();
    action();
  };

  return (
    <>
      <PhotoKebab
        ref={triggerRef}
        type="button"
        $open={!!position}
        onClick={toggle}
        aria-label={`Options for headshot ${index + 1}`}
        aria-haspopup="menu"
        aria-expanded={!!position}
      >
        <MoreVertIcon width="12" height="12" />
      </PhotoKebab>
      {position &&
        createPortal(
          <ContextMenuSurface
            ref={menuRef}
            role="menu"
            onMouseDown={event => event.stopPropagation()}
            style={{
              top: position.y,
              left: position.x,
              zIndex: ENLARGED_HOVER_PREVIEW_Z + 1,
            }}
          >
            {index === 0 ? (
              <ContextMenuItem
                role="menuitem"
                $disabled
                aria-disabled
                title="This headshot appears on nametags and quote cards."
                onClick={event => event.preventDefault()}
              >
                <StarFilledIcon />
                Primary Headshot
              </ContextMenuItem>
            ) : (
              <ContextMenuItem role="menuitem" onClick={run(onSetPrimary)}>
                <StarFilledIcon />
                Set as Primary
              </ContextMenuItem>
            )}
            <ContextMenuItem $danger role="menuitem" onClick={run(onRemove)}>
              <DeleteIcon />
              Remove Headshot
            </ContextMenuItem>
          </ContextMenuSurface>,
          document.body,
        )}
    </>
  );
};

const SparkleLoading = () => (
  <SimpleLoadingSpinner
    text={null}
    icon={<SparkleIcon />}
    spinnerSize={20}
    spinnerWidth={2}
  />
);

const SIZE = 56;

// Fixed width, so the fields beside it never move as headshots come and go;
// a little room either side.
const HeadshotFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-xs);
  width: ${SIZE + 12}px;
  flex-shrink: 0;
`;

// The same label the shared Input/Textarea primitives draw.
const HeadshotLabel = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-xs);
  color: var(--text-secondary);
`;

// One under another, centred in the column.
const HeadshotList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: ${SIZE + 12}px;
  padding-top: 4px;
`;

const PhotoFrame = styled.div<{ $primary: boolean }>`
  position: relative;
  width: ${SIZE}px;
  height: ${SIZE}px;
  flex-shrink: 0;
  border-radius: 50%;
`;

// The primary's 1px edge is gold (`--accent`) instead of the hairline grey:
// the same weight as every other "selected" mark in the app (selected chips),
// so it reads as chosen without shouting.
const Photo = styled.img<{ $primary: boolean }>`
  width: ${SIZE}px;
  height: ${SIZE}px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid
    ${({ $primary }) => ($primary ? 'var(--accent)' : 'var(--border)')};
  display: block;
`;

// The ⋮ on a photo's top-right edge: a small dark disc so it reads over any
// photo, shown on hover or keyboard focus, and while its menu is open.
const PhotoKebab = styled(KebabButton)<{ $open: boolean }>`
  position: absolute;
  top: -4px;
  right: -4px;
  margin: 0;
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid var(--border);
  background: var(--background);
  color: var(--text-secondary);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition:
    opacity var(--transition-fast),
    color var(--transition-fast);

  ${PhotoFrame}:hover &,
  &:focus-visible {
    opacity: 1;
  }
  &:hover {
    background: var(--background);
    color: var(--foreground);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
`;

// The one add control: a dashed circle the size of a headshot, the camera in
// it. Bordered-field hover escalation (docs/design-system.md → Form Fields).
const AddTile = styled.button`
  width: ${SIZE}px;
  height: ${SIZE}px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px dashed var(--border);
  background: var(--input-background);
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition:
    border-color var(--transition-fast),
    color var(--transition-fast);

  &:hover {
    border-color: var(--text-muted);
    color: var(--text-secondary);
  }
`;
